import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, RotateCcw, Wind, Check } from "lucide-react";
import { Header } from "../components/Header";
import { EASE } from "../components/Reveal";

const QUESTIONS = [
    { q: "Do you notice a white or yellow film on your tongue?", tip: "A coated tongue is the number-one source — scrape it gently each morning (about ten seconds).", options: [["Yes, often", 0], ["Sometimes", 1], ["Rarely or never", 2]] },
    { q: "How does your mouth feel when you wake up?", tip: "Overnight dryness fuels morning breath — keep water by the bed and breathe through your nose.", options: [["Very dry", 0], ["A little dry", 1], ["Normal", 2]] },
    { q: "Do you breathe through your mouth, especially at night?", tip: "Mouth breathing dries out saliva — your mouth's natural rinse. Try nasal breathing, especially during sleep.", options: [["Yes, often", 0], ["Sometimes", 1], ["No", 2]] },
    { q: "How often do you clean your tongue?", tip: "Add a tongue scrape to your morning routine — it is the single highest-impact habit on this list.", options: [["Never", 0], ["Now and then", 1], ["Every day", 2]] },
    { q: "How often do you clean between your teeth (floss or water flosser)?", tip: "Odour hides between teeth — clean between them every night; floss or a water flosser both work.", options: [["Rarely", 0], ["A few times a week", 1], ["Every night", 2]] },
    { q: "How much water do you drink on a typical day?", tip: "A hydrated mouth simply doesn't smell the way a dry one does — sip water through the day.", options: [["1–2 glasses", 0], ["3–5 glasses", 1], ["6 or more", 2]] },
    { q: "Most days, do you have coffee, alcohol, or smoke?", tip: "Coffee, alcohol and smoking all dry the mouth — go easy, and rinse with water afterwards.", options: [["Two or more of these", 0], ["One of them", 1], ["None", 2]] },
    { q: "Do your gums bleed when you brush?", tip: "Bleeding gums can signal plaque buildup — brush twice daily, and consider a dental check if it persists.", options: [["Often", 0], ["Sometimes", 1], ["No", 2]] },
];

const MAX = QUESTIONS.length * 2;

const tierOf = (score) =>
    score >= 75
        ? { label: "Fresh & on track", color: "#5A7A5A", blurb: "Your habits are doing most of the work already. Keep the routine and you'll stay ahead of it." }
        : score >= 45
        ? { label: "A few leaks in the routine", color: "#B89052", blurb: "You're close — two or three small habits are letting odour build. Fix those and you'll notice the difference within days." }
        : { label: "Time for a reset", color: "#A65656", blurb: "Nothing here is permanent — your answers point to the classic causes, and each one has a simple fix. Start with the tips below." };

const BreathScorePage = () => {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState([]);
    const done = step >= QUESTIONS.length;

    const answer = (value) => {
        setAnswers((a) => [...a, value]);
        setStep((s) => s + 1);
    };

    const reset = () => { setStep(0); setAnswers([]); };

    const score = Math.round((answers.reduce((a, b) => a + b, 0) / MAX) * 100);
    const tier = tierOf(score);
    const tips = QUESTIONS
        .map((q, i) => ({ value: answers[i] ?? 2, tip: q.tip }))
        .filter((t) => t.value < 2)
        .sort((a, b) => a.value - b.value)
        .slice(0, 3)
        .map((t) => t.tip);

    return (
        <div data-testid="breath-score-page" className="min-h-screen bg-[#FDFCF8]">
            <Header />
            <main className="max-w-[760px] mx-auto px-4 sm:px-6 py-14 sm:py-20">
                <div className="text-center">
                    <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#718370]">
                        <Wind size={14} strokeWidth={1.8} /> Free interactive tool
                    </p>
                    <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1D1A]">Breath Score Calculator</h1>
                    <p className="mx-auto mt-4 max-w-[520px] text-sm sm:text-base leading-relaxed text-[#6D736D]">
                        Eight honest questions, sixty seconds, one score — and the exact habits to fix first. Nothing is stored or sent anywhere.
                    </p>
                </div>

                <div className="mt-10 rounded-[20px] border border-[#E4DFD3] bg-white p-7 sm:p-10 shadow-[0_24px_60px_-30px_rgba(34,37,34,0.25)]">
                    {!done && (
                        <div className="mb-8">
                            <div className="flex items-center justify-between text-xs font-semibold text-[#6D736D]">
                                <span data-testid="score-progress-label">Question {step + 1} of {QUESTIONS.length}</span>
                                <span>{Math.round((step / QUESTIONS.length) * 100)}%</span>
                            </div>
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#F5F2EA]">
                                <motion.div
                                    className="h-full rounded-full bg-[#576856]"
                                    animate={{ width: `${(step / QUESTIONS.length) * 100}%` }}
                                    transition={{ duration: 0.5, ease: EASE }}
                                />
                            </div>
                        </div>
                    )}

                    <AnimatePresence mode="wait">
                        {!done ? (
                            <motion.div
                                key={step}
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -40 }}
                                transition={{ duration: 0.4, ease: EASE }}
                            >
                                <h2 data-testid={`score-question-${step + 1}`} className="font-serif text-xl sm:text-2xl font-semibold text-[#1A1D1A]">
                                    {QUESTIONS[step].q}
                                </h2>
                                <div className="mt-6 space-y-3">
                                    {QUESTIONS[step].options.map(([label, value], oi) => (
                                        <button
                                            key={oi}
                                            data-testid={`score-option-${step + 1}-${oi + 1}`}
                                            onClick={() => answer(value)}
                                            className="flex w-full items-center justify-between rounded-xl border border-[#E4DFD3] bg-[#FDFCF8] px-5 py-3.5 text-left text-sm font-medium text-[#1A1D1A] transition-all duration-300 hover:border-[#576856] hover:bg-[#F5F2EA] active:scale-[0.99]"
                                        >
                                            {label}
                                            <span className="h-2 w-2 rounded-full bg-[#C4BCA8]" />
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="result"
                                data-testid="score-result"
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, ease: EASE }}
                                className="text-center"
                            >
                                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#6D736D]">Your breath score</p>
                                <motion.p
                                    data-testid="score-value"
                                    className="mt-2 font-serif text-7xl font-bold"
                                    style={{ color: tier.color }}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2, duration: 0.7, ease: EASE }}
                                >
                                    {score}
                                </motion.p>
                                <span className="mt-3 inline-block rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white" style={{ background: tier.color }}>
                                    {tier.label}
                                </span>
                                <p className="mx-auto mt-4 max-w-[440px] text-sm leading-relaxed text-[#6D736D]">{tier.blurb}</p>

                                {tips.length > 0 && (
                                    <div className="mx-auto mt-8 max-w-[480px] rounded-xl border border-[#E4DFD3] bg-[#F5F2EA] p-6 text-left">
                                        <p className="text-xs font-bold uppercase tracking-wider text-[#576856]">Start with these</p>
                                        <ul className="mt-3 space-y-3">
                                            {tips.map((tip, i) => (
                                                <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-[#1A1D1A]">
                                                    <Check size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-[#576856]" />
                                                    {tip}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                                    <Link to="/" className="rounded-full bg-[#576856] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1A1D1A]">
                                        Read the complete guide
                                    </Link>
                                    <button
                                        data-testid="score-retake-btn"
                                        onClick={reset}
                                        className="inline-flex items-center gap-2 rounded-full border border-[#C4BCA8] px-6 py-2.5 text-sm font-semibold text-[#576856] transition-colors hover:bg-[#F5F2EA]"
                                    >
                                        <RotateCcw size={14} /> Retake
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <Link to="/tools" className="mt-8 inline-flex items-center gap-1.5 text-xs font-medium text-[#6D736D] transition-colors hover:text-[#1A1D1A]">
                    <ArrowLeft size={13} /> All free tools
                </Link>
            </main>
        </div>
    );
};

export default BreathScorePage;
